import { useRef, useState } from 'react';
import { useModals } from '@customerio/pluma-components/react';
import { PLATFORM_LABELS, type AppCredentialValue, type PushPlatform } from '@/features/push-credentials';
import type { App } from '@/services/db/app/entity';
import { PlatformSetupModal, type PlatformSetupModalData } from '../components/platform-setup-modal';
import {
  APP_PLATFORMS,
  arePlatformsComplete,
  arePlatformsDirty,
  clearDraft,
  disablePlatform,
  effectivePayload,
  initialDrafts,
  initialIdentifierFor,
  isConfigured,
  isDisabledOnSave,
  isEnabled,
  isPending,
  restoreSavedPlatform,
  reuseFor,
  rowIdentifier,
  savedPlatformOf,
  stageCredential,
  statusColor,
  statusLabel,
  type PlatformDrafts,
  type PlatformStatusColor,
} from '../utils/platform-state';

export type PlatformRow = {
  platform: PushPlatform;
  label: string;
  identifier: string;
  isEnabled: boolean;
  statusLabel: string;
  statusColor: PlatformStatusColor;
  isPending: boolean;
  isMenuReadOnly: boolean;
  isEditReadOnly: boolean;
  isDisableReadOnly: boolean;
};

export type UsePlatformDraftsArgs = {
  app: App | null;
  canEdit: boolean;
  canDelete: boolean;
  canSaveForm: boolean;
};

export function usePlatformDrafts({ app, canEdit, canDelete, canSaveForm }: UsePlatformDraftsArgs) {
  const modals = useModals();
  const [drafts, setDrafts] = useState<PlatformDrafts>(() => initialDrafts(app));

  // A second click while the setup form is open would stack two of them.
  const isSetupOpenRef = useRef(false);

  const openSetup = async (platform: PushPlatform, isReplace: boolean) => {
    if (isSetupOpenRef.current) {
      return;
    }
    isSetupOpenRef.current = true;
    try {
      const data: PlatformSetupModalData = {
        platform,
        isReplace,
        initialIdentifier: initialIdentifierFor(app, drafts, platform),
        ...reuseFor(app, drafts, platform),
      };
      const id = modals.open(PlatformSetupModal, data);
      const result = await modals.waitForClose(id);
      if (result.reason === 'confirm' && result.data != null) {
        const credential: AppCredentialValue = result.data;
        setDrafts((current) => stageCredential(current, platform, credential));
      }
    } finally {
      isSetupOpenRef.current = false;
    }
  };

  const enablePlatform = (platform: PushPlatform) => {
    const restored = restoreSavedPlatform(app, drafts, platform);
    if (restored == null) {
      void openSetup(platform, false);
      return;
    }
    setDrafts(restored);
  };

  const rows: PlatformRow[] = APP_PLATFORMS.map((platform) => buildRow(platform));

  function buildRow(platform: PushPlatform): PlatformRow {
    const configured = isConfigured(app, drafts, platform);
    // Removing a platform the backend holds issues a delete, so it needs delete
    // as well as edit; dropping one that was only staged needs no more than the
    // permission that allowed staging it.
    const isDisableReadOnly = savedPlatformOf(app, platform).present ? !(canEdit && canDelete) : !canSaveForm;
    return {
      platform,
      label: PLATFORM_LABELS[platform],
      identifier: rowIdentifier(app, drafts, platform),
      isEnabled: isEnabled(drafts, platform),
      statusLabel: statusLabel(configured),
      statusColor: statusColor(configured),
      isPending: isPending(app, drafts, platform),
      // Disabling the trigger on edit permission alone would strand a
      // create-only role that had just staged a platform: Disable is open to
      // them, but the menu holding it would not open.
      isMenuReadOnly: !canEdit && isDisableReadOnly,
      isEditReadOnly: !canEdit,
      isDisableReadOnly,
    };
  }

  return {
    rows,
    isDirty: arePlatformsDirty(app, drafts),
    isComplete: arePlatformsComplete(app, drafts),
    payloads: {
      ios: effectivePayload(drafts, 'ios'),
      android: effectivePayload(drafts, 'android'),
      disableIos: isDisabledOnSave(app, drafts, 'ios'),
      disableAndroid: isDisabledOnSave(app, drafts, 'android'),
    },
    enablePlatform,
    editPlatform: (platform: PushPlatform) => void openSetup(platform, true),
    disablePlatform: (platform: PushPlatform) => setDrafts((current) => disablePlatform(current, platform)),
    clearDraft: (platform: PushPlatform) => setDrafts((current) => clearDraft(current, platform)),
    reset: (source: App | null) => setDrafts(initialDrafts(source)),
  };
}

export type PlatformDraftsController = ReturnType<typeof usePlatformDrafts>;
